// Imports for external libraries go here.
import { FC, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { RichText, Button, CheckBox, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { CardOverviewProps } from './CardOverview.types';
import { StyledCardOverview } from './CardOverview.styles';
import { CardOverviewHorizontal } from '../../molecules/CardOverviewHorizontal';
import { useStore } from '../../modules/store/cobrandStore';
import { isMobile, replaceWhitespaceAndAddBtn, shouldCardDisplay } from '../../modules/utils';
import { CardArtBadge } from '../../molecules/CardArtBadge';
import { CARD_LAYOUT_TYPE, COBRAND_CARD_TYPE } from '../../modules/constants/constants';
import { CobrandImage } from '../../molecules/CobrandImage/CobrandImage';

export const CardOverviewConfig = {
  emptyLabel: 'CardOverview',
  isEmpty: () => true,
  resourceType: `/components/content/cardoverview`,
};

export const CardOverviewComp: FC<CardOverviewProps> = (props: CardOverviewProps) => {
  const {
    cardType,
    imagePath,
    imagePathWithoutBadge,
    cardArtIcon,
    cardArtText,
    displayCardArtIcon,
    altText,
    header,
    pointSectionSeparator,
    pointSectionDescription,
    cardSectionSeparator,
    cardSectionDescription,
    linkText,
    linkUrl,
    openInaNewTab,
    componentId,
    customClass,
    styleclass,
    variation,
    isCardComparisonFeatureEnabled,
    cardCode,
    setSelectedCards,
    tertiaryLinks,
    enable2CardLayout,
    addSpaceToCardArt,
    trackingProperties,
    cardHeader,
    layout,
  } = props;

  const { cobrandData, setCobrandData } = useStore(store => store);
  const [ComparisonCheckBox, setComparisonCheckBox] = useState(false);
  const isViewportL = useCheckBreakpoint('viewportL');
  const ref = useRef<HTMLDivElement>(null);

  const isMobileDevice: boolean = isMobile();
  const isVerticalLayout = variation === CARD_LAYOUT_TYPE.VERTICALLAYOUT;
  const isHorizontalLayout = variation === CARD_LAYOUT_TYPE.HORIZONTALLAYOUT;

  useEffect(() => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    setComparisonCheckBox(isChecked);
  }, [cobrandData.selectedCards, cardCode]);

  const linkType = openInaNewTab ? 'external' : 'internal';
  const setDisableState = () => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    return isViewportL && !isMobileDevice
      ? cobrandData.selectedCards.length === 3 && !isChecked
      : cobrandData.selectedCards.length === 2 && !isChecked;
  };

  const handleCheckboxChange = () => {
    setComparisonCheckBox(prev => {
      const newCheckState = !prev;
      const updatedCards = newCheckState
        ? [
            ...cobrandData.selectedCards,
            {
              cardname: cardCode,
              checked: newCheckState,
              cardType: cardType,
              imagePath: imagePathWithoutBadge ? imagePathWithoutBadge : imagePath,
              header: cardHeader,
            },
          ]
        : cobrandData.selectedCards.filter(({ cardname }) => cardname !== cardCode);

      setCobrandData({ ...cobrandData, selectedCards: updatedCards });
      if (setSelectedCards) {
        setSelectedCards();
      }
      return newCheckState;
    });
  };

  const showCardArt = () => {
    return (
      variation !== CARD_LAYOUT_TYPE.BLPLAYOUT &&
      (displayCardArtIcon?.toString() === 'true' || addSpaceToCardArt === 'true')
    );
  };
  const getCssClass = () => {
    const isBusinessCard = cardType === COBRAND_CARD_TYPE.BUSINESSCARDTYPE;
    return layout === '5-card'
      ? 'card-overview-no-border-image'
      : isVerticalLayout
      ? 'card-overview-foc-image'
      : `card-overview-blp-image ${isBusinessCard ? 'card-overview-blp-image--margin' : ''}`;
  };

  // Check if this component is inside CobrandContainer with tab functionality
  const isInCobrandContainerWithTabs = props.column === '3';

  if (isHorizontalLayout) {
    return (
      <CardOverviewHorizontal data-testid="cardoverview" data-component-name="o-cobrand-cardoverview" {...props} />
    );
  }

  return (
    <StyledCardOverview
      data-testid="cardoverview"
      data-component-name="o-cobrand-cardoverview"
      id={componentId}
      className={clsx(
        'card-overview justify-content-center d-md-flex flex-wrap',
        isVerticalLayout ? 'col-12 p-0' : '',
        `${styleclass || ''}`,
        isInCobrandContainerWithTabs && 'd-md-block',
        isInCobrandContainerWithTabs && (shouldCardDisplay(cardType, cobrandData.activeTabValue) ? 'd-block' : 'd-none')
      )}
      data-cardtype={cardType}
    >
      <div className={clsx(isVerticalLayout ? 'card-overview-foc-wrapper mt-2 mt-md-1 mt-lg-0' : '', 'd-flex')}>
        <div
          className={clsx(
            'card-overview-wrapper justify-content-center d-flex',
            enable2CardLayout === 'true' || layout === '5-card' ? '' : 'pt-5 pt-md-0',
            layout === '5-card' ? '' : enable2CardLayout === 'true' ? 'mt-3' : isVerticalLayout && 'mt-5 mt-md-3'
          )}
        >
          <div
            className={clsx(
              'd-flex flex-grow-1 justify-content-center',
              layout === '5-card' ? '' : 'mt-0 pt-0 mt-md-5 pt-md-5'
            )}
          >
            <div
              className={clsx(
                isVerticalLayout ? 'px-3 pt-2 pt-md-0' : 'mx-lg-1 card-overview-bordered__default px-md-3 px-2',
                `card-overview-bordered text-center pb-4 `,
                'd-flex flex-column justify-content-between',
                pointSectionSeparator || cardSectionSeparator === 'true' ? 'card-overview-seperator' : '',
                't-background-color',
                layout === '5-card' ? 'card-overview-no-border px-lg-0' : '',
                layout === '5-card' && cardType === COBRAND_CARD_TYPE.BUSINESSCARDTYPE
                  ? 'card-overview-business-card'
                  : '',
                customClass,
                styleclass
              )}
            >
              <div className={clsx('d-flex flex-column flex-grow-1')}>
                <div
                  className={clsx(
                    isVerticalLayout ? 'px-0 card-overview-bordered__image' : '',
                    'px-lg-0 mx-auto mt-1 mt-sm-2'
                  )}
                >
                  {showCardArt() ? (
                    <CardArtBadge
                      imagePath={imagePath}
                      imageClass={'col-12 p-0'}
                      badgeClass={'mx-2'}
                      defaultImageText={altText}
                      badgeDetails={`<div class='d-flex align-items-center justify-content-center badge-txt-align'>
                          <div><span class="${cardArtIcon && `${cardArtIcon.split(':')[1]} m-0 pr-1`}"></span></div>
                          <div class='t-font-xs t-overline-overlay'>${cardArtText?.toUpperCase()}</div>
                            </div>`}
                      customClass={clsx(getCssClass())}
                      addSpaceToCardArt={addSpaceToCardArt}
                      displayCardArtIcon={displayCardArtIcon}
                    />
                  ) : (
                    <CobrandImage
                      defaultImageURL={imagePath}
                      altText={altText}
                      customClass={clsx(getCssClass())}
                    ></CobrandImage>
                  )}
                </div>
                {header && header.length && (
                  <RichText
                    componentId={`header-${componentId}`}
                    text={header}
                    customClass={clsx(
                      'mx-auto mt-3',
                      isVerticalLayout ? 'card-overview-foc-header' : 'card-overview-header'
                    )}
                  />
                )}
                {pointSectionSeparator === 'true' && <hr className={clsx('mb-3 mx-0 row')} />}
                {pointSectionDescription && pointSectionDescription.length && (
                  <RichText
                    componentId={`pointsSection-${componentId}`}
                    text={pointSectionDescription}
                    customClass={clsx(
                      'd-flex flex-column margin-zero',
                      'card-overview-subheader',
                      isVerticalLayout ? 'card-overview-foc flex-grow-1' : ''
                    )}
                  />
                )}
                {cardSectionSeparator === 'true' && <hr className={clsx('mt-3 mb-4 mx-0 row')} />}
                {cardSectionDescription && cardSectionDescription.length && (
                  <RichText
                    componentId={`cardSection-${componentId}`}
                    text={cardSectionDescription}
                    customClass={clsx('d-flex flex-column flex-grow-1')}
                  />
                )}
                {tertiaryLinks && tertiaryLinks?.length && (
                  <RichText
                    componentId={`tertiaryLinks-${componentId}`}
                    customClass="tertiaryLinks_styles"
                    text={tertiaryLinks}
                  />
                )}
              </div>
              <div>
                <Button
                  id={`${componentId}-btn`}
                  isLink={true}
                  href={linkUrl}
                  target={openInaNewTab === 'true' ? '_blank' : '_self'}
                  /* "track-completed" class is added to escape getNonCoBrandTrackValue method in clientlib.js */
                  className={clsx(isVerticalLayout ? 'mt-2 mt-lg-0' : '', 'm-button-m mt-md-3 mb-0 mt-0')}
                  trackingProperties={trackingProperties}
                  buttonCopy={linkText}
                  linkType={linkType}
                  ref={ref}
                  linkAriaLabelOpenNewWindow={openInaNewTab ? 'Open in new tab' : ''}
                ></Button>
              </div>
              {isCardComparisonFeatureEnabled && (
                <div className={'checkbox-wrapper flex-align-center mt-4'}>
                  <CheckBox
                    checkboxLabel={`Compare`}
                    aria-label={'compare-' + replaceWhitespaceAndAddBtn(componentId)}
                    onChange={handleCheckboxChange}
                    checked={ComparisonCheckBox}
                    checkboxName={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                    checkboxId={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                    disabled={setDisableState()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledCardOverview>
  );
};
export const CardOverview = (props: CardOverviewProps) => {
  return (
    <EditableComponent config={CardOverviewConfig} {...props}>
      <CardOverviewComp {...props} />
    </EditableComponent>
  );
};
